import type { E164PhoneNumber } from "@redotech/phone/number";
import {
  convertArbitraryPhoneNumberToE164,
  isValidE164PhoneNumber,
} from "@redotech/phone/number";
import type { AiToneAndVoiceSettings } from "@redotech/redo-concierge-settings-common/ai-tone-and-voice";
import type { IntakeAnswers } from "../../../registry/types";
import type {
  ConciergeBusinessInfo,
  ConciergeEscalation,
  ConciergeMissingMerchantField,
  ConciergePhoneStatus,
  ConciergeProfileInfo,
  PhoneValidationField,
} from "./types";

export const MISSING_FIELD_LABELS: Partial<
  Record<ConciergeMissingMerchantField, string>
> = {
  street1: "Street address",
  city: "City",
  state: "State",
  zip: "ZIP code",
  country: "Country",
  adminName: "Business owner name",
  adminPhone: "Business owner phone",
};

export const MISSING_FIELD_PLACEHOLDERS: Partial<
  Record<ConciergeMissingMerchantField, string>
> = { adminName: "Tony Ramirez", adminPhone: "(555) 123-4567" };

export function needsAddress(
  missingFields: readonly ConciergeMissingMerchantField[],
): boolean {
  return (
    missingFields.includes("street1") ||
    missingFields.includes("city") ||
    missingFields.includes("state") ||
    missingFields.includes("zip") ||
    missingFields.includes("country")
  );
}

export function isPhoneStepValid({
  businessInfo,
  missingMerchantFields,
  profileInfo,
}: {
  businessInfo: ConciergeBusinessInfo;
  missingMerchantFields: readonly ConciergeMissingMerchantField[];
  profileInfo: ConciergeProfileInfo;
}): boolean {
  return (
    getPhoneStepValidationMessages({
      businessInfo,
      missingMerchantFields,
      profileInfo,
    }).length === 0
  );
}

export function getPhoneStepValidationMessage({
  businessInfo,
  missingMerchantFields,
  profileInfo,
}: {
  businessInfo: ConciergeBusinessInfo;
  missingMerchantFields: readonly ConciergeMissingMerchantField[];
  profileInfo: ConciergeProfileInfo;
}): { field: PhoneValidationField; message: string } | null {
  return (
    getPhoneStepValidationMessages({
      businessInfo,
      missingMerchantFields,
      profileInfo,
    })[0] ?? null
  );
}

export function getPhoneStepValidationMessages({
  businessInfo,
  missingMerchantFields,
  profileInfo,
}: {
  businessInfo: ConciergeBusinessInfo;
  missingMerchantFields: readonly ConciergeMissingMerchantField[];
  profileInfo: ConciergeProfileInfo;
}): { field: PhoneValidationField; message: string }[] {
  const errors: { field: PhoneValidationField; message: string }[] = [];
  if (!businessInfo.legalEntityName.trim()) {
    errors.push({
      field: "legalEntityName",
      message: "Enter your legal entity name.",
    });
  }
  const ein = businessInfo.ein.trim();
  if (!ein) {
    errors.push({ field: "ein", message: "Enter your 9-digit EIN." });
  } else if (!/^\d{9}$/.test(ein)) {
    errors.push({ field: "ein", message: "EIN must be exactly 9 digits." });
  }
  const supportEmail = businessInfo.supportEmail?.trim() ?? "";
  if (supportEmail && !isValidEmailAddress(supportEmail)) {
    errors.push({
      field: "supportEmail",
      message: "Enter a valid support email.",
    });
  }
  if (
    missingMerchantFields.includes("adminPhone") &&
    !getNormalizedAdminPhone(profileInfo.adminPhone)
  ) {
    errors.push({
      field: "adminPhone",
      message: "Enter a valid business owner phone number.",
    });
  }
  return errors;
}

export function getNormalizedAdminPhone(
  phoneNumber: string,
): E164PhoneNumber | undefined {
  const e164PhoneNumber = convertArbitraryPhoneNumberToE164(phoneNumber);
  return e164PhoneNumber && isValidE164PhoneNumber(e164PhoneNumber)
    ? e164PhoneNumber
    : undefined;
}

function isValidEmailAddress(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function getEscalationValidationMessage(
  escalation: ConciergeEscalation,
): string | null {
  if (escalation.escalateToSupport === null) {
    return "Choose what happens when Commerce Agent can't help.";
  }
  if (escalation.escalateToSupport) {
    return null;
  }
  const supportEmail = escalation.supportEmail.trim();
  if (!supportEmail) {
    return "Enter the support email shoppers should write to.";
  }
  return isValidEmailAddress(supportEmail)
    ? null
    : "Enter a valid support email.";
}

export function toEscalationConfigInput(escalation: ConciergeEscalation): {
  escalateToSupport: boolean;
  contactEmail?: string;
} {
  const escalateToSupport = escalation.escalateToSupport === true;
  return escalateToSupport
    ? { escalateToSupport: true }
    : {
        escalateToSupport: false,
        contactEmail: escalation.supportEmail.trim(),
      };
}

export function buildIntakeAnswers({
  personaName,
  phoneStatus,
}: {
  personaName: string;
  phoneStatus: ConciergePhoneStatus;
}): IntakeAnswers {
  return {
    personaName: personaName.trim(),
    phoneProvisioningStatus: phoneStatus,
  };
}

export function updateToneRange<
  K extends keyof Pick<
    AiToneAndVoiceSettings,
    | "formalityCasualness"
    | "seriousnessHumor"
    | "respectfulnessIrreverence"
    | "matterOfFactEnthusiasm"
  >,
>(
  current: AiToneAndVoiceSettings,
  key: K,
  value: [number, number],
): AiToneAndVoiceSettings {
  return { ...current, [key]: { min: value[0], max: value[1] } };
}

export function toToneSettingsInput(settings: AiToneAndVoiceSettings) {
  return {
    voiceInstructions: settings.voiceInstructions,
    formalityCasualness: {
      min: settings.formalityCasualness.min,
      max: settings.formalityCasualness.max,
    },
    seriousnessHumor: {
      min: settings.seriousnessHumor.min,
      max: settings.seriousnessHumor.max,
    },
    respectfulnessIrreverence: {
      min: settings.respectfulnessIrreverence.min,
      max: settings.respectfulnessIrreverence.max,
    },
    matterOfFactEnthusiasm: {
      min: settings.matterOfFactEnthusiasm.min,
      max: settings.matterOfFactEnthusiasm.max,
    },
  };
}
